import { PaginationResult } from '@/models/pagination.model';
import {
  BaseService,
  Status,
  DataResponse,
  MessageResponse,
  MessageData,
} from './base.service';
import { LearningItem } from '@/models/learning-item.model';

type ServerLearningItem = LearningItem & { languages: string };

class LearningItemService extends BaseService {
  private url: string = '/api/learning-item';

  constructor() {
    super();
  }

  public async create(item: LearningItem): Promise<DataResponse<LearningItem>> {
    const res = await this.$http.post<LearningItem>(
      this.url,
      Object.assign(item, { languages: item.languages.join(',') }),
    );

    return {
      status: [Status.Ok, Status.Created].includes(res.status),
      data: res.data,
    };
  }

  public async update(item: LearningItem): Promise<DataResponse<LearningItem>> {
    const res = await this.$http.put<LearningItem>(
      `${this.url}/${item.id}`,
      Object.assign(item, { languages: [...(item.languages || [])].join(',') }),
    );

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async publish(
    id: number,
    published: boolean,
  ): Promise<DataResponse<LearningItem>> {
    const res = await this.$http.put<LearningItem>(`${this.url}/publish`, {
      id,
      published,
    });

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async get(id: number): Promise<DataResponse<LearningItem>> {
    const res = await this.$http.get<ServerLearningItem>(`${this.url}/${id}`);
    return {
      status: res.status === Status.Ok,
      data: Object.assign(res.data, {
        languages: (res.data.languages as string)
          .split(',')
          .filter((v: string) => !!v),
      }),
    };
  }

  public async getList(): Promise<DataResponse<LearningItem[]>> {
    const res = await this.$http.get<ServerLearningItem[]>(`${this.url}`);
    return {
      status: res.status === Status.Ok,
      data: res.data.map((i) =>
        Object.assign(i, {
          languages: (i.languages as string)
            .split(',')
            .filter((v: string) => !!v),
        }),
      ),
    };
  }

  public async delete(id: number): Promise<MessageResponse> {
    const res = await this.$http.delete<MessageData>(`${this.url}/${id}`);

    return {
      status: res.status === Status.Ok,
      message: res.data.message,
    };
  }

  public async upload(csv: File): Promise<DataResponse<LearningItem[]>> {
    const data = new FormData();
    data.append('file', csv);

    const res = await this.$http.post<LearningItem[]>(
      `${this.url}/upload`,
      data,
      true,
    );
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async search(
    search: string,
    page: number = 0,
    categoryId?: number,
  ): Promise<PaginationResult<LearningItem>> {
    const res = await this.$http.get<PaginationResult<LearningItem>>(
      `${this.url}/paginated?search=${search}${
        categoryId ? `&categories=${categoryId}` : ''
      }&page=${page}&count=10`,
    );

    return res.data;
  }
}

export default new LearningItemService();
